import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import {
  buildDownloadLink,
  DEFAULT_LINK_ENVELOPE_LIMITS,
  encryptLinkEnvelope,
  encryptedLinkArtifactSize,
  generateLinkCapabilities,
  LINK_LIFETIME_MS,
  prepareLinkEnvelope,
  type LinkInputFile,
  type PreparedLinkEnvelope,
} from "../../link-protocol";
import {
  createLinkApiClient,
  LinkApiError,
  type LinkApiClient,
  type LinkTransferProgress,
} from "./linkApiClient";
import {
  deleteLocalLinkRecord,
  markLocalLinkRevoked,
  saveLocalLinkRecord,
  type LocalLinkRecord,
} from "./linkRecords";
import {
  copyShareLink,
  formatLinkBytes,
  formatLinkExpiry,
  shareOrCopyLink,
} from "./uiUtils";
import { useLinkServiceStatus } from "./useLinkServiceStatus";
import "./linkShare.css";

export interface LinkSharePanelProps {
  files: readonly LinkInputFile[];
  apiClient?: LinkApiClient;
  baseUrl?: string | URL;
  onLinkCreated?: (record: LocalLinkRecord) => void;
}

type ShareStep = "encrypting" | "uploading" | "confirming" | "revoking";

type PreparationState =
  | { kind: "empty" }
  | { kind: "waiting" }
  | { kind: "working" }
  | {
      kind: "ready";
      envelope: PreparedLinkEnvelope;
      encryptedBytes: number;
    }
  | { kind: "error"; message: string };

type ShareState =
  | { kind: "disclosure" }
  | {
      kind: "working";
      step: ShareStep;
      progress?: LinkTransferProgress;
    }
  | { kind: "ready"; record: LocalLinkRecord; localSaveWarning: boolean }
  | { kind: "revoked"; record: LocalLinkRecord }
  | { kind: "error"; message: string };

const stepLabels: Readonly<Record<ShareStep, string>> = {
  encrypting: "Encrypting in this browser",
  uploading: "Uploading encrypted file",
  confirming: "Confirming temporary storage",
  revoking: "Revoking",
};

function displayNameFor(files: readonly LinkInputFile[]): string {
  if (files.length === 1) {
    return files[0]?.name ?? "1 file";
  }
  return `${String(files.length)} files`;
}

function errorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "Link creation was cancelled. No share link was created.";
  }
  if (error instanceof LinkApiError) {
    if (error.code === "payload_too_large") {
      return "The prepared encrypted file exceeds the 10 MB link limit.";
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "The link could not be created.";
}

export function LinkSharePanel({
  files,
  apiClient,
  baseUrl,
  onLinkCreated,
}: LinkSharePanelProps) {
  const [pickedFiles, setPickedFiles] = useState<LinkInputFile[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [preparation, setPreparation] = useState<PreparationState>({
    kind: "empty",
  });
  const [state, setState] = useState<ShareState>({ kind: "disclosure" });
  const [notice, setNotice] = useState("");
  const abortController = useRef<AbortController | null>(null);
  const client = useMemo(() => apiClient ?? createLinkApiClient(), [apiClient]);
  const service = useLinkServiceStatus(client);
  const selectedFiles = files.length > 0 ? files : pickedFiles;
  const totalBytes = selectedFiles.reduce((sum, file) => sum + file.blob.size, 0);

  useEffect(() => {
    let active = true;
    abortController.current?.abort();
    abortController.current = null;
    const resetTimer = window.setTimeout(() => {
      setAcknowledged(false);
      setState({ kind: "disclosure" });
      setNotice("");
      if (selectedFiles.length === 0) {
        setPreparation({ kind: "empty" });
        return;
      }
      if (service.status === null) {
        setPreparation({ kind: "waiting" });
        return;
      }

      setPreparation({ kind: "working" });
      const overheadBytes = encryptedLinkArtifactSize(0);
      void prepareLinkEnvelope(selectedFiles, {
        ...DEFAULT_LINK_ENVELOPE_LIMITS,
        maxBytes: service.status.maxBytes - overheadBytes,
      })
        .then((envelope) => {
          if (active) {
            setPreparation({
              kind: "ready",
              envelope,
              encryptedBytes: encryptedLinkArtifactSize(envelope.preparedBytes),
            });
          }
        })
        .catch((error: unknown) => {
          if (active) {
            setPreparation({ kind: "error", message: errorMessage(error) });
          }
        });
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(resetTimer);
    };
  }, [selectedFiles, service.status]);

  useEffect(
    () => () => {
      abortController.current?.abort();
    },
    [],
  );

  function handlePickerChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextFiles = [...(event.currentTarget.files ?? [])].map((file) => ({
      name: file.name,
      type: file.type,
      blob: file,
    }));
    setPickedFiles(nextFiles);
    event.currentTarget.value = "";
  }

  async function handleCreateLink(): Promise<void> {
    if (
      !acknowledged ||
      selectedFiles.length === 0 ||
      service.status === null ||
      preparation.kind !== "ready"
    ) {
      return;
    }

    const controller = new AbortController();
    abortController.current = controller;
    setNotice("");
    try {
      setState({ kind: "working", step: "encrypting" });
      const encrypted = await encryptLinkEnvelope(preparation.envelope.blob);
      if (encrypted.byteLength !== preparation.encryptedBytes) {
        throw new Error("The encrypted upload size changed unexpectedly.");
      }
      const capabilities = generateLinkCapabilities();

      setState({
        kind: "working",
        step: "uploading",
        progress: { loaded: 0, total: encrypted.byteLength },
      });
      const uploaded = await client.uploadEncryptedArtifact({
        artifact: encrypted.artifact,
        accessToken: capabilities.accessToken,
        revokeToken: capabilities.revokeToken,
        ciphertextSha256: encrypted.ciphertextSha256,
        signal: controller.signal,
        onProgress(progress) {
          setState({ kind: "working", step: "uploading", progress });
        },
      });

      setState({ kind: "working", step: "confirming" });
      const shareUrl = buildDownloadLink(baseUrl ?? window.location.origin, {
        accessToken: capabilities.accessToken,
        decryptionKey: encrypted.decryptionKey,
      });
      const recordInput = {
        shareUrl,
        accessToken: capabilities.accessToken,
        revokeToken: capabilities.revokeToken,
        decryptionKey: encrypted.decryptionKey,
        displayName: displayNameFor(selectedFiles),
        fileCount: selectedFiles.length,
        ciphertextBytes: uploaded.ciphertextBytes,
        ciphertextSha256: uploaded.ciphertextSha256,
        expiresAt: uploaded.expiresAt,
        environment: uploaded.environment,
        createdAt: new Date(
          Date.parse(uploaded.expiresAt) - LINK_LIFETIME_MS,
        ).toISOString(),
      } as const;

      let record: LocalLinkRecord;
      let localSaveWarning = false;
      try {
        record = await saveLocalLinkRecord(recordInput);
      } catch {
        localSaveWarning = true;
        record = {
          ...recordInput,
          id: crypto.randomUUID(),
          status: "active",
        };
      }

      setState({ kind: "ready", record, localSaveWarning });
      onLinkCreated?.(record);
    } catch (error) {
      setState({ kind: "error", message: errorMessage(error) });
    } finally {
      abortController.current = null;
    }
  }

  async function handleCopy(record: LocalLinkRecord): Promise<void> {
    try {
      await copyShareLink(record.shareUrl);
      setNotice("Link copied.");
    } catch {
      setNotice("The browser could not copy the link. Select it manually.");
    }
  }

  async function handleShare(record: LocalLinkRecord): Promise<void> {
    try {
      const outcome = await shareOrCopyLink(record.shareUrl);
      setNotice(outcome === "shared" ? "Share sheet opened." : "Link copied.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      setNotice("The browser could not share the link.");
    }
  }

  async function handleRevoke(record: LocalLinkRecord): Promise<void> {
    const confirmed = window.confirm(
      "Revoke this link now? Future downloads will stop, but copies already saved cannot be removed.",
    );
    if (!confirmed) {
      return;
    }

    setState({ kind: "working", step: "revoking" });
    try {
      await client.revoke(record.accessToken, record.revokeToken);
      let updated: LocalLinkRecord = {
        ...record,
        status: "revoked",
      };
      try {
        updated = (await markLocalLinkRevoked(record.id)) ?? updated;
      } catch {
        setNotice("The cloud copy was revoked, but the local status was not saved.");
      }
      setState({ kind: "revoked", record: updated });
    } catch (error) {
      setState({ kind: "error", message: errorMessage(error) });
    }
  }

  async function handleRemoveRecord(record: LocalLinkRecord): Promise<void> {
    if (
      record.status === "active" &&
      !window.confirm(
        "Remove only the local link record? This does not revoke the encrypted cloud copy.",
      )
    ) {
      return;
    }
    try {
      await deleteLocalLinkRecord(record.id);
    } catch {
      // An upload whose IndexedDB write failed has only an in-memory record.
    }
    setAcknowledged(false);
    setState({ kind: "disclosure" });
    setNotice("Local link record removed.");
  }

  const activeRecord =
    state.kind === "ready" || state.kind === "revoked" ? state.record : null;
  const isLocalSimulation =
    service.status?.environment === "local-simulation" ||
    activeRecord?.environment === "local-simulation";

  return (
    <section className="link-panel" aria-labelledby="link-share-title">
      <div className="link-panel__heading">
        <p className="link-panel__eyebrow">Temporary encrypted delivery</p>
        <h2 id="link-share-title">Send via link</h2>
        <p>
          Encrypt files in this browser, then temporarily store only the encrypted copy
          for up to 72 hours.
        </p>
      </div>

      {isLocalSimulation && (
        <div className="link-banner link-banner--warning" role="status">
          Local development simulation. This server keeps encrypted artifact bytes only
          in memory; it does not contact Cloudflare, and restarting the server removes
          every simulated link.
        </div>
      )}

      {service.status === null && (
        <div
          className={`link-banner${
            service.error === null ? "" : " link-banner--warning"
          }`}
          role="status"
        >
          {service.error ?? "Checking the temporary link service environment."}
        </div>
      )}

      {files.length === 0 && state.kind === "disclosure" && (
        <label className="link-file-picker">
          <span>Select files</span>
          <input type="file" multiple onChange={handlePickerChange} />
          <small>
            Any file type, up to{" "}
            {formatLinkBytes(service.status?.maxBytes ?? 10 * 1024 * 1024)} after local
            packaging.
          </small>
        </label>
      )}

      {selectedFiles.length > 0 && state.kind === "disclosure" && (
        <div className="link-disclosure">
          <div className="link-file-summary">
            <strong>{displayNameFor(selectedFiles)}</strong>
            <span>
              Original size: {formatLinkBytes(totalBytes)} across{" "}
              {String(selectedFiles.length)}{" "}
              {selectedFiles.length === 1 ? "file" : "files"}
            </span>
          </div>
          {preparation.kind === "waiting" && (
            <p>Waiting for the temporary service limit before preparation.</p>
          )}
          {preparation.kind === "working" && (
            <p role="status">
              Preparing the exact upload package locally. No file bytes have left this
              device.
            </p>
          )}
          {preparation.kind === "error" && (
            <div className="link-error" role="alert">
              <h3>These files cannot be prepared</h3>
              <p>{preparation.message}</p>
            </div>
          )}
          {preparation.kind === "ready" && (
            <>
              <div className="link-file-summary">
                <strong>Prepared encrypted upload</strong>
                <span>{formatLinkBytes(preparation.encryptedBytes)}</span>
              </div>
              <ul>
                <li>
                  This exact-size encrypted copy will leave this device over the
                  internet.
                </li>
                <li>
                  New downloads stop at the shown 72-hour server cutoff, or earlier if
                  you revoke the link.
                </li>
                <li>
                  {service.status?.environment === "local-simulation"
                    ? "The local simulation removes expired in-memory links during access checks and forgets every link when its server restarts."
                    : "Cloudflare KV automatically expires the encrypted copy at that cutoff; a new link can take up to about one minute to become downloadable worldwide."}
                </li>
                <li>
                  Anyone with the complete link can download and decrypt the files while
                  it is active.
                </li>
              </ul>
              <label className="link-confirmation">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => {
                    setAcknowledged(event.currentTarget.checked);
                  }}
                />
                <span>
                  I understand anyone with this link can download the file until it
                  expires or I revoke it
                </span>
              </label>
              <button
                className="link-button link-button--primary"
                type="button"
                disabled={!acknowledged || service.status === null}
                onClick={() => {
                  void handleCreateLink();
                }}
              >
                Encrypt and create link
              </button>
            </>
          )}
        </div>
      )}

      {state.kind === "working" && (
        <div className="link-progress" aria-live="polite">
          <span className="link-status-chip">{stepLabels[state.step]}</span>
          <h3>{stepLabels[state.step]}</h3>
          {state.progress === undefined ? (
            <p>Please keep this page open.</p>
          ) : (
            <>
              <progress value={state.progress.loaded} max={state.progress.total} />
              <p>
                {formatLinkBytes(state.progress.loaded)} of{" "}
                {formatLinkBytes(state.progress.total)}
              </p>
            </>
          )}
          {state.step !== "revoking" && (
            <button
              className="link-button link-button--secondary"
              type="button"
              onClick={() => {
                abortController.current?.abort();
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {state.kind === "ready" && (
        <div className="link-ready" aria-live="polite">
          <span className="link-status-chip link-status-chip--success">Link ready</span>
          <h3>Encrypted file uploaded</h3>
          <p>
            Anyone with this link can download and decrypt the file until the shown
            expiry time. Revoking prevents future downloads, but it cannot remove copies
            someone already saved.
          </p>
          {!isLocalSimulation && (
            <p>
              Cloudflare may need up to about one minute to make a new encrypted copy
              available in every region. The receiver can leave the download open while
              it retries.
            </p>
          )}
          <p className="link-expiry">
            Available until{" "}
            <time dateTime={state.record.expiresAt}>
              {formatLinkExpiry(state.record.expiresAt)}
            </time>
          </p>
          <label className="link-url">
            <span>Complete private link</span>
            <input readOnly value={state.record.shareUrl} />
          </label>
          {state.localSaveWarning && (
            <div className="link-banner link-banner--warning" role="alert">
              The link works, but its controls could not be saved to this browser. Copy
              the link and keep this page open if you may need to revoke it.
            </div>
          )}
          <div className="link-actions">
            <button
              className="link-button link-button--primary"
              type="button"
              onClick={() => {
                void handleCopy(state.record);
              }}
            >
              Copy link
            </button>
            <button
              className="link-button link-button--secondary"
              type="button"
              onClick={() => {
                void handleShare(state.record);
              }}
            >
              Share link
            </button>
            <button
              className="link-button link-button--danger"
              type="button"
              onClick={() => {
                void handleRevoke(state.record);
              }}
            >
              Revoke link
            </button>
          </div>
        </div>
      )}

      {state.kind === "revoked" && (
        <div className="link-ready" aria-live="polite">
          <span className="link-status-chip">Revoked</span>
          <h3>Future downloads have stopped</h3>
          <p>
            The temporary encrypted copy was deleted. This cannot remove a copy a
            recipient already saved.
          </p>
        </div>
      )}

      {state.kind === "error" && (
        <div className="link-error" role="alert">
          <span className="link-status-chip link-status-chip--danger">
            Needs attention
          </span>
          <h3>Link action failed</h3>
          <p>{state.message}</p>
          <button
            className="link-button link-button--secondary"
            type="button"
            onClick={() => {
              setState({ kind: "disclosure" });
            }}
          >
            Try again
          </button>
        </div>
      )}

      {activeRecord !== null && (
        <button
          className="link-button link-button--quiet"
          type="button"
          onClick={() => {
            void handleRemoveRecord(activeRecord);
          }}
        >
          Delete local link record
        </button>
      )}
      {notice && (
        <p className="link-notice" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}
